// Copyright 2026, Auxilio Technologies LLC

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { feedbackAPI } from '../services/api';

const Feedback: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [feedbackType, setFeedbackType] = useState<string>('');
  const [featureArea, setFeatureArea] = useState<string>('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Feedback types available for selection
  const feedbackTypes = [
    { id: '', name: t('feedback.feedbackTypes.select', 'Select feedback type...') },
    { id: 'general_comment', name: t('feedback.feedbackTypes.generalComment', 'General Comment') },
    { id: 'issue', name: t('feedback.feedbackTypes.issue', 'Report an Issue') },
    { id: 'enhancement', name: t('feedback.feedbackTypes.enhancement', 'Suggest Enhancement') },
  ];

  // Logistics-specific feature areas
  const featureAreas = [
    { id: '', name: t('feedback.featureAreas.select', 'Select feature area...') },
    { id: 'dashboard', name: t('feedback.featureAreas.dashboard', 'Dashboard') },
    { id: 'control_tower', name: t('feedback.featureAreas.controlTower', 'Control Tower') },
    { id: 'shipments', name: t('feedback.featureAreas.shipments', 'Shipments') },
    { id: 'route_optimization', name: t('feedback.featureAreas.routeOptimization', 'Route Optimization') },
    { id: 'alerts', name: t('feedback.featureAreas.alerts', 'Alerts') },
    { id: 'forecasting', name: t('feedback.featureAreas.forecasting', 'Forecasting') },
    { id: 'supplier_scorecard', name: t('feedback.featureAreas.supplierScorecard', 'Supplier Scorecard') },
    { id: 'analytics', name: t('feedback.featureAreas.analytics', 'Analytics') },
    { id: 'sites', name: t('feedback.featureAreas.sites', 'Sites') },
    { id: 'user_management', name: t('feedback.featureAreas.userManagement', 'User Management') },
    { id: 'settings', name: t('feedback.featureAreas.settings', 'Settings') },
    { id: 'general', name: t('feedback.featureAreas.general', 'General') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!comment.trim()) {
      setErrorMessage(t('feedback.errors.commentRequired', 'Please enter your feedback'));
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await feedbackAPI.submit({
        feedback_type: feedbackType || undefined,
        feature_area: featureArea || undefined,
        comment: comment.trim(),
      });

      setSubmitStatus('success');
      // Reset form after successful submission
      setFeedbackType('');
      setFeatureArea('');
      setComment('');

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setErrorMessage(t('feedback.errors.submitFailed', 'Failed to submit feedback. Please try again.'));
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFeedbackType('');
    setFeatureArea('');
    setComment('');
    setSubmitStatus('idle');
    setErrorMessage('');
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('feedback.title', 'Feedback')}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {t('feedback.subtitle', 'Help us improve Auxilio Logistics by sharing your thoughts')}
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-start gap-3">
          <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
              {t('feedback.success', 'Thank you for your feedback!')}
            </h3>
            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
              {t('feedback.successMessage', 'Your feedback has been submitted successfully. We appreciate your input.')}
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
          <ExclamationCircleIcon className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              {t('feedback.error', 'Error')}
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              {errorMessage}
            </p>
          </div>
        </div>
      )}

      {/* Feedback Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('feedback.formTitle', 'Submit Feedback')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('feedback.formDescription', 'Tell us about your experience or suggest improvements')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Feedback Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('feedback.feedbackTypeLabel', 'Feedback Type')}
              <span className="text-gray-400 dark:text-gray-500 ml-1 font-normal">
                ({t('feedback.optional', 'optional')})
              </span>
            </label>
            <select
              value={feedbackType}
              onChange={(e) => setFeedbackType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {feedbackTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Feature Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('feedback.featureAreaLabel', 'Feature Area')}
              <span className="text-gray-400 dark:text-gray-500 ml-1 font-normal">
                ({t('feedback.optional', 'optional')})
              </span>
            </label>
            <select
              value={featureArea}
              onChange={(e) => setFeatureArea(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {featureAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('feedback.commentLabel', 'Your Feedback')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('feedback.commentPlaceholder', 'Please describe your feedback, suggestion, or issue in detail...')}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleReset}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('feedback.submitting', 'Submitting...')}
                </>
              ) : (
                t('feedback.submit', 'Submit Feedback')
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Tips Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('feedback.tipsTitle', 'Tips for Great Feedback')}
          </h2>
        </div>
        <div className="p-6">
          <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-teal-500 font-bold">•</span>
              <span>{t('feedback.tip1', 'Be specific about what feature or area you are referring to')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-500 font-bold">•</span>
              <span>{t('feedback.tip2', 'For issues, include steps to reproduce the problem')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-500 font-bold">•</span>
              <span>{t('feedback.tip3', 'For enhancements, describe how the feature would help your workflow')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-500 font-bold">•</span>
              <span>{t('feedback.tip4', 'Include any relevant context about your role or use case')}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
